import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Space, Button, Input, Modal, Badge } from 'antd';
import { UploadOutlined, SearchOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import AssetTypeImportModal from '../../components/Admin/AssetTypeImportModal';
import { useAuth } from '../../context/AuthContext';
import { useNotificationApi } from '../../context/NotificationContext';
import dayjs from 'dayjs';

const { Search } = Input;

const Assets = () => {
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isImportModalVisible, setIsImportModalVisible] = useState(false);
    const [searchText, setSearchText] = useState('');
    
    const { hasPermission, PERMISSION_LEVELS } = useAuth();
    const notificationApi = useNotificationApi();

    const canManageAssets = hasPermission('settings', 'edit', PERMISSION_LEVELS.ALL);

    const showError = useCallback((title, content, key = 'globalError') => {
        notificationApi.destroy(key);
        try {
          notificationApi.error({
            key,
            message: title,
            description: content,
            duration: 4.5,
            placement: 'topRight',
            style: {
              width: 400,
              zIndex: 10000,
            },
          });
        } catch (err) {
          console.error("Notification failed, falling back to Modal.error:", err);
          Modal.error({
            title,
            content,
            centered: true,
            zIndex: 99999,
          });
        }
    }, [notificationApi]);

    const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
        notificationApi.success({
          key,
          message: title,
          description: description,
          duration: 3,
          placement: 'topRight',
          style: {
            width: 400,
            zIndex: 10000,
          },
        });
    }, [notificationApi]);

    const showLoading = useCallback((key, messageContent) => {
        let dismissNotification;
        const delay = 300;
        const minDisplayTime = 500;
    
        const startTime = Date.now();
    
        const timer = setTimeout(() => {
          dismissNotification = notificationApi.open({
            key,
            message: messageContent,
            duration: 0,
            placement: 'topRight',
            style: {
              width: 400,
              zIndex: 10000,
            },
          });
        }, delay);
    
        return () => {
          clearTimeout(timer);
          const elapsed = Date.now() - startTime;
          const remaining = Math.max(0, minDisplayTime - elapsed);
    
          setTimeout(() => {
            if (dismissNotification) {
              typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
            } else {
              notificationApi.destroy(key);
            }
          }, remaining);
        };
    }, [notificationApi]);

    const fetchAssets = useCallback(async () => {
        setLoading(true);
        const dismissLoading = showLoading('fetchAssets', 'Loading assets...');
        try {
            const response = await api.get('/api/assets');
            let fetchedAssets = response.data.data || [];

            const filtered = searchText
                ? fetchedAssets.filter(asset => {
                    const searchLower = searchText.toLowerCase();
                    return (
                        (asset.host_name && asset.host_name.toLowerCase().includes(searchLower)) ||
                        (asset.serial_number && asset.serial_number.toLowerCase().includes(searchLower)) ||
                        (asset.asset_type && asset.asset_type.toLowerCase().includes(searchLower)) ||
                        (asset.ip_address && asset.ip_address.toLowerCase().includes(searchLower))
                    );
                })
                : fetchedAssets;

            setAssets(filtered);
            showSuccess('Assets Loaded', `Successfully fetched ${filtered.length} assets.`);
        } catch (error) {
            console.error('Failed to fetch assets:', error);
            const errorMessage = error.response?.data?.message || 'Error fetching assets.';
            const errorDetails = process.env.NODE_ENV === 'development' ? error.response?.data?.error : null;
            showError(
                'Loading Error',
                <span>
                    {errorMessage}
                    {errorDetails && <div>Details: {errorDetails}</div>}
                </span>,
                'fetchAssetsError'
            );
        } finally {
            setLoading(false);
            dismissLoading();
        }
    }, [searchText, showLoading, showSuccess, showError]);

    useEffect(() => {
        fetchAssets();
    }, [fetchAssets]);

    const handleImportSuccess = (importData) => {
        const successMsg = importData.added !== undefined 
            ? `Import completed: ${importData.added} added, ${importData.updated} updated`
            : 'Assets imported successfully';
        showSuccess('Import Completed', successMsg);
        fetchAssets(); // Refresh the asset list
    };

    const columns = [
        { 
            title: 'ID', 
            dataIndex: 'id', 
            key: 'id',
            width: 80,
            sorter: (a, b) => a.id - b.id,
            defaultSortOrder: 'ascend',
        },
        { 
            title: 'Asset Type', 
            dataIndex: 'asset_type', 
            key: 'asset_type',
            sorter: (a, b) => (a.asset_type || '').localeCompare(b.asset_type || ''),
            filters: [
                ...new Set(assets.map(item => item.asset_type).filter(Boolean))
            ].map(type => ({ text: type, value: type })),
            onFilter: (value, record) => record.asset_type === value,
            render: (text) => text || 'N/A'
        },
        { 
            title: 'Model', 
            dataIndex: 'model', 
            key: 'model',
            sorter: (a, b) => (a.model || '').localeCompare(b.model || ''),
            render: (text) => text || 'N/A'
        },
        { 
            title: 'Serial Number', 
            dataIndex: 'serial_number', 
            key: 'serial_number',
            sorter: (a, b) => (a.serial_number || '').localeCompare(b.serial_number || ''),
            render: (text) => text === 'N/A' ? <Badge color="blue" text="N/A" /> : text || 'N/A'
        },
        { 
            title: 'Host Name', 
            dataIndex: 'host_name', 
            key: 'host_name',
            sorter: (a, b) => (a.host_name || '').localeCompare(b.host_name || ''),
            render: (text) => text || 'N/A'
        },
        { 
            title: 'IP Address', 
            dataIndex: 'ip_address', 
            key: 'ip_address',
            sorter: (a, b) => (a.ip_address || '').localeCompare(b.ip_address || ''),
            render: (text) => text || 'N/A'
        },
    ];

    return (
        <Card
            title="Asset Management"
            extra={
                <Space>
                    <Search
                        placeholder="Search assets..."
                        allowClear
                        enterButton={<SearchOutlined />}
                        size="middle"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onSearch={fetchAssets}
                        style={{ width: 250 }}
                    />
                    <Button 
                        type="primary"
                        icon={<UploadOutlined />}
                        onClick={() => setIsImportModalVisible(true)}
                        disabled={!canManageAssets}
                    >
                        Import Assets
                    </Button>
                </Space>
            }
        >
            <Table
                columns={columns}
                dataSource={assets}
                rowKey="id"
                loading={loading}
                pagination={{ 
                    pageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (total) => `Total ${total} assets`
                }}
                scroll={{ x: true }}
                bordered
            />

            <AssetTypeImportModal
                visible={isImportModalVisible}
                onCancel={() => setIsImportModalVisible(false)}
                onSuccess={handleImportSuccess}
            />
        </Card>
    );
};

export default Assets;